import { useAssessments } from '@/app/redux/hooks/assessments';
import { useCustomLevels } from '@/app/redux/hooks/customLevels';
import { useGroups } from '@/app/redux/hooks/groups';
import { useLearnerNote } from '@/app/redux/hooks/learnerNotes';
import { useLessonPlans } from '@/app/redux/hooks/lessonPlans';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { useWordMats } from '@/app/redux/hooks/wordMats';
import UpgradeButton from '@/common/ui/UpgradeButton';
import {
  getPaymentPastDueStatus,
  getSeatLimitStatus,
} from '@/common/utils/license';
import { CaretRightIcon, HouseIcon, WarningIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { SidebarTrigger } from '@shadcn/components/sidebar';
import { Skeleton } from '@shadcn/components/skeleton';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface Breadcrumb {
  icon?: JSX.Element;
  name: string | null;
  url: string;
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const { studentsById, isLoading: studentsLoading } = useStudents();
  const { organisation } = useOrganisation();
  const { groupsById, isLoading: groupsLoading } = useGroups();
  const { assessmentsById, isLoading: assessmentsLoading } = useAssessments();
  const { wordMatsById, isLoading: wordMatsLoading } = useWordMats();
  const { lessonPlansById, isLoading: lessonPlansLoading } = useLessonPlans();
  const { customLevels, isLoading: customLevelsLoading } = useCustomLevels();

  const { groupId, studentId, assessmentId, id, noteId } = useParams<{
    groupId?: string;
    studentId?: string;
    assessmentId?: string;
    id?: string;
    noteId?: string;
  }>();
  const { learnerNote, isLoading: noteLoading } = useLearnerNote(noteId);

  const seatLimitStatus = useMemo(() => {
    return getSeatLimitStatus(
      organisation?.license,
      organisation?.studentCount,
    );
  }, [organisation]);

  const paymentPastDueStatus = useMemo(() => {
    return getPaymentPastDueStatus(organisation?.license);
  }, [organisation]);

  const showUpgradeButton =
    organisation &&
    !organisation.activationChecklist.isDismissed &&
    (organisation?.license.plan === 'trial' ||
      organisation?.license.plan === 'free');

  const breadcrumbs: Breadcrumb[] = useMemo(() => {
    function getBreadcrumbName(path: string): string | null {
      if (path === groupId) {
        return groupsById.get(groupId)?.name ?? (groupsLoading ? null : path);
      }

      if (path === studentId) {
        const student = studentsById.get(studentId);
        if (student)
          return `${student.studentFirstName} ${student.studentSurname}`;
        return studentsLoading ? null : path;
      }

      if (path === assessmentId) {
        const assessment = assessmentsById.get(assessmentId);
        if (assessment)
          return dayjs(assessment.createdAt).format('DD/MM/YY h:mm a');
        return assessmentsLoading ? null : path;
      }

      if (path === id && id !== 'new') {
        const wordMat = wordMatsById.get(id);
        if (wordMat) return wordMat.name;
        const lessonPlan = lessonPlansById.get(id);
        if (lessonPlan) return lessonPlan.name;
        const customLevel = customLevels.find((l) => l.id === id);
        if (customLevel) return customLevel.description || 'Untitled Level';
        return wordMatsLoading || lessonPlansLoading || customLevelsLoading
          ? null
          : path;
      }

      if (path === noteId && noteId !== 'new') {
        if (learnerNote) return learnerNote.name;
        return noteLoading ? null : path;
      }

      // Default cases
      switch (path) {
        case 'dashboard':
          return 'Dashboard';
        case 'groups':
          return 'Groups';
        case 'learners':
          return 'Learners';
        case 'scopeandsequence':
          return 'Scope & Sequence';
        case 'assessments':
          return 'Assessments';
        case 'activity':
          return 'Activity';
        case 'decodables':
          return 'Decodable Readers';
        case 'worksheet':
          return 'Worksheet';
        case 'settings':
          return 'Settings';
        case 'profile':
          return 'Profile';
        case 'my-organisations':
          return 'My Organisations';
        case 'organisation':
          return 'Organisation Details';
        case 'members':
          return 'Organisation Members';
        case 'billing':
          return 'Billing & Subscription';
        case 'bulkadd':
          return 'Bulk Add Learners';
        case 'insights':
          return 'Insights';
        case 'learner-settings':
          return 'Default Learner Settings';
        case 'wordmats':
          return 'Word Mats';
        case 'lessonplans':
          return 'Lesson Plans';
        case 'customlevels':
          return 'Custom Levels';
        case 'feature-requests':
          return 'Feature Requests';
        case 'notes':
          return 'Notes';
        case 'changelog':
          return 'Changelog';
        case 'new':
          return 'New';
        default:
          return path;
      }
    }

    const paths = pathname.split('/').filter((p) => p !== '');
    const newBreadcrumbs: Breadcrumb[] = [];

    let index = 0;
    for (const path of paths) {
      const breadcrumb: Breadcrumb = {
        icon: path.includes('dashboard') ? <HouseIcon size={20} /> : undefined,
        name: getBreadcrumbName(path),
        url: `/${paths.slice(0, index + 1).join('/')}`,
      };
      newBreadcrumbs.push(breadcrumb);
      index++;
    }

    return newBreadcrumbs;
  }, [
    pathname,
    groupsById,
    studentsById,
    assessmentsById,
    wordMatsById,
    lessonPlansById,
    groupId,
    studentId,
    assessmentId,
    id,
    noteId,
    learnerNote,
    groupsLoading,
    studentsLoading,
    assessmentsLoading,
    wordMatsLoading,
    lessonPlansLoading,
    customLevels,
    customLevelsLoading,
    noteLoading,
  ]);

  return (
    <NavbarContainer>
      <SidebarTrigger />

      <BreadcrumbContainer>
        {breadcrumbs.map((crumb, index) => (
          <CrumbContainer key={index}>
            <Crumb
              onClick={() => {
                navigate(crumb.url);
              }}
            >
              {crumb.icon ??
                (crumb.name !== null ? (
                  crumb.name
                ) : (
                  <Skeleton className="h-3 w-20" />
                ))}
            </Crumb>
            {index < breadcrumbs.length - 1 && <CaretRightIcon size={16} />}
          </CrumbContainer>
        ))}
      </BreadcrumbContainer>

      <div className="flex items-center gap-2 mr-2 ml-auto">
        {(seatLimitStatus === 'over-limit' ||
          seatLimitStatus === 'grace-ended') && (
          <Alert
            variant="warning"
            className="w-fit p-1 cursor-pointer"
            onClick={() => navigate('/dashboard/settings/billing')}
          >
            <WarningIcon size={18} className="ml-0.5" />
            <AlertDescription className="text-sm pr-2">
              You're over your learner limit
            </AlertDescription>
          </Alert>
        )}
        {(paymentPastDueStatus === 'past-due' ||
          paymentPastDueStatus === 'grace-ended') && (
          <Alert
            variant="destructive"
            className="w-fit p-1 cursor-pointer"
            onClick={() => navigate('/dashboard/settings/billing')}
          >
            <WarningIcon size={18} className="ml-0.5" />
            <AlertDescription className="text-sm pr-2">
              There was a problem processing your payment
            </AlertDescription>
          </Alert>
        )}
        {showUpgradeButton && (
          <UpgradeButton onClick={() => navigate('/pricing')} />
        )}
      </div>
    </NavbarContainer>
  );
}

const NavbarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  width: 100%;
  padding: 4px 8px;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Crumb = styled.div`
  color: #444;
  font-weight: 400;
  font-size: 0.9em;
  letter-spacing: -0.025em;
  margin-left: 4px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
